import clsx from "clsx";
import styles from "./CoverBleed.module.scss";

export interface CoverBleedProps {
  /** The artwork to bleed. Absent ⇒ renders NOTHING, so the host keeps whatever ground it already paints. */
  url?: string | null;
  className?: string;
}

/**
 * A release's own artwork as the card's ground — blurred, saturated and over-scaled until it is a field of
 * that record's colour rather than a picture of it.
 *
 * Two absolutely-positioned layers, for the host to drop inside any box that is `position: relative` +
 * `overflow: hidden` + `isolation: isolate`. It is a COMPONENT rather than a mixin because both release
 * heroes need it and their frames are otherwise unrelated — the hub banner is a `DarkHero` and the builder
 * header is hand-rolled with its own reference metrics, so sharing the FRAME would mean forcing one of them
 * onto the other's proportions. The treatment is the part that has to be identical.
 *
 * <b>Why two layers and not one background stack.</b> A CSS filter applies to an element's own
 * pseudo-elements too, so a scrim stacked on the blurred layer is blurred with it. The photo therefore owns
 * the filter and the scrim is a SIBLING above — the layering the reference's own `__bg` (z −2) / `__scrim`
 * (z −1) already used.
 */
/**
 * The class the HOST box must carry alongside these layers — it re-points the on-inverse text ramp to the
 * bleed one, so every descendant (stat labels, eyebrow, meta, count) steps up without any of them naming a
 * token. Exported rather than applied here because the type it governs is the host's own content, which is a
 * sibling of these layers: nothing rendered by this component is an ancestor of it.
 */
export const coverBleedHost: string = styles.host;

export function CoverBleed({ url, className }: CoverBleedProps) {
  if (!url) return null;
  return (
    <>
      <div
        className={clsx(styles.art, className)}
        aria-hidden="true"
        style={{ backgroundImage: `url("${encodeURI(url)}")` }}
      />
      <div className={styles.scrim} aria-hidden="true" />
    </>
  );
}
